import { XMarkIcon } from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'

import useAbortControllers from '@/hooks/AbortControllers'
import { getRepairOrderTimeline } from '@/services/repairOrders.service'
import { formatDate, formatDateTime } from '@/utils/formatters'
import { stripHtmlTags } from '@/utils/helpers'

// Audit values arrive as strings; detect ISO-ish dates so we can pretty-print
// them with the same formatters the rest of the timeline uses.
const DATETIME_PATTERN = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

// Turn a snake_case or camelCase field name into a readable label.
const humanizeField = (field) =>
  field
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/^./, (char) => char.toUpperCase())

// Audit values arrive as strings (rich-text fields as HTML). Pretty-print
// dates, strip markup, fall back to an em dash for empty/null, and truncate
// very long values.
const formatValue = (value) => {
  if (value === null || value === undefined || value === '' || value === 'None') return '—'
  const text = String(value)
  if (DATETIME_PATTERN.test(text)) return formatDateTime(text)
  if (DATE_PATTERN.test(text)) return formatDate(text)
  const stripped = stripHtmlTags(text).trim()
  if (!stripped) return '—'
  return stripped.length > 120 ? `${stripped.slice(0, 120)}…` : stripped
}

/**
 *
 * RepairOrderTimeline
 *
 * Inline side panel (sibling of the chat panel, sharing its width and dark
 * styling) showing the merged activity timeline for a repair order, newest
 * first. Read-only, and the page stays interactive while it's open. Backed by
 * GET /api/v1/repair-orders/<id>/timeline/.
 */
const RepairOrderTimeline = ({ repairOrderId, onClose }) => {
  const [events, setEvents] = useState([])
  const [next, setNext] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [loaded, setLoaded] = useState(false)
  // A single 'timeline' request stream: a newer fetch (or unmount) aborts the
  // previous in-flight one, so a superseded/post-unmount response can't flip
  // state. The service treats an aborted request as a no-op (returns null
  // without touching setLoading/setError), which is why no isMounted ref is
  // needed here.
  const { getSignal } = useAbortControllers()

  useEffect(() => {
    if (!repairOrderId) return

    // Clear the previous RO's history so switching ROs with the panel open
    // doesn't briefly show stale events while the new request is in flight.
    setEvents([])
    setNext(null)
    setLoaded(false)

    const load = async () => {
      setError(null)
      const response = await getRepairOrderTimeline(
        `/repair-orders/${repairOrderId}/timeline/?limit=50`,
        setLoading,
        setError,
        getSignal('timeline'),
      )
      if (response) {
        setEvents(response.results)
        setNext(response.next)
        setLoaded(true)
      }
    }

    load()
  }, [repairOrderId, getSignal])

  const loadMore = async () => {
    if (!next) return
    const response = await getRepairOrderTimeline(
      next,
      setLoading,
      setError,
      getSignal('timeline'),
    )
    if (response) {
      setEvents((prev) => [...prev, ...response.results])
      setNext(response.next)
    }
  }

  return (
    // Mirror the Chat panel's container exactly: a relative shell with the
    // content absolutely pinned inside it. Keeping the content out of normal
    // flow lets this panel shrink within the parent flex column so the page
    // header (with the Hide History toggle / close) stays visible on mobile —
    // a plain `flex h-full` panel's intrinsic height pushed the header offscreen.
    <aside className="bg-midnight-950/90 relative ml-0 h-full w-full shadow-xl md:min-w-[489px]">
      <div className="absolute inset-y-0 right-0 flex w-full grow flex-col">
        <div className="flex items-start justify-between px-4 py-6 sm:px-6">
          <div>
            <h2 className="text-xl font-semibold text-white">History</h2>
            <p className="text-charcoal-light text-sm">
              Every recorded change on this repair order.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="focus-visible:outline-hidden ml-3 rounded-md"
          >
            <span className="sr-only">Close panel</span>
            <XMarkIcon aria-hidden="true" className="size-6 fill-white" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6 sm:px-6">
          {error && <p className="text-red py-2 text-sm font-semibold">{error}</p>}

          {loaded && events.length === 0 && !loading && (
            <p className="text-charcoal-light py-8 text-sm">No activity recorded yet.</p>
          )}

          <ol className="relative ml-1 border-l border-white/20">
            {events.map((event) => {
              const changeEntries = Object.entries(event.changes || {})
              // Creation events carry an initial-value snapshot (old is always
              // null), so render them as a plain "Field: value" list rather than
              // a from→to diff.
              const isCreate = event.action === 'created'

              return (
                <li key={event.id} className="mb-6 ml-4">
                  <div className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border-2 border-white bg-blue-500" />
                  <time className="text-charcoal-light text-xs font-semibold uppercase tracking-wide">
                    {formatDateTime(event.timestamp)}
                  </time>
                  <p className="text-sm font-medium text-white">{event.description}</p>
                  <p className="text-charcoal-light text-xs">by {event.actor}</p>

                  {changeEntries.length > 0 && (
                    <ul className="mt-2 space-y-1 rounded-md bg-white/10 p-2">
                      {changeEntries.map(([field, value]) => {
                        const [oldValue, newValue] = Array.isArray(value) ? value : ['', value]
                        return (
                          <li key={field} className="text-xs">
                            <span className="font-semibold text-white">
                              {humanizeField(field)}:{' '}
                            </span>
                            {!isCreate && (
                              <>
                                <span className="text-charcoal-light line-through">
                                  {formatValue(oldValue)}
                                </span>
                                <span className="text-charcoal-light mx-1">→</span>
                              </>
                            )}
                            <span className="text-white">{formatValue(newValue)}</span>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </li>
              )
            })}
          </ol>

          {loading && <p className="text-charcoal-light py-2 text-sm">Loading…</p>}

          {next && !loading && (
            <button
              type="button"
              onClick={loadMore}
              className="text-sm font-semibold text-blue-300 hover:text-blue-200"
            >
              Load more
            </button>
          )}
        </div>
      </div>
    </aside>
  )
}

RepairOrderTimeline.propTypes = {
  repairOrderId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
}

RepairOrderTimeline.defaultProps = {
  repairOrderId: null,
}

export default RepairOrderTimeline
